// ============================================================
// optimisticCache — DnD系（移動/入替/リサイズ）の楽観更新ライフサイクル
//
// 2026-07-29: 「移動したのに元に戻る」事象の修正で新設。
//
// 従来 useMoveSwap / useReservationSave がそれぞれ持っていた
// 「キャッシュを直接 set してリスナーを叩く」だけの patch 関数を1本化し、
// **開始 → 確定 → ロールバック** のライフサイクルを明示する。
//
// なぜ必要か（回帰の原因）:
//   楽観更新はローカルで updated_at を進めない。一方 ReservationSyncService の
//   updateCache は「サーバーの方が新しいか同時刻なら上書き」なので、
//   保存が確定する前に届いたサーバー取得（= 保存前スナップショット、
//   同じ updated_at）が楽観更新を素通しで潰していた。
//   同期ウィンドウ拡大（240+1095日 / 那覇 7,841件・20MB・約5秒）で
//   この取得が数秒間宙に浮くようになり、事象が頻発するようになった。
//
// 対策は2段:
//   1. beginLocalWrite で保存中の id を保護し、その間サーバー取得を無視する
//   2. 保存成功時に **サーバーレスポンス** を applyLocalUpdate でキャッシュへ入れ、
//      updated_at をサーバー正本に進めてから保護を解除する
//      （= 他の保存経路と同じ battle-tested パターン。VehiclePositionMap /
//        DayListReservations / ReservationTable / MobileReservationCard と同じ）
//
// 仕様書: md/master_md/ref-incidents.md「カレンダー移動の巻き戻り」
//         md/master_md/feature-realtime.md
// ============================================================

import { Reservation } from './types';
// @ts-ignore — JS 実装のサービス
import { reservationSyncService } from '../../services/reservationSyncService';

/** キャッシュへ書き込み、全リスナーへ「変更なし通知」で再描画させる */
function writeAndNotify(updates: Reservation[]): void {
  for (const r of updates) {
    reservationSyncService.cachedReservations.set(String(r.id), r);
  }
  const all = reservationSyncService.getReservations();
  reservationSyncService.listeners.forEach((cb: Function) => cb([], all));
}

/**
 * 楽観更新を適用し、保存確定まで同期による上書きから保護する。
 * 必ず対になる settleOptimistic / rollbackOptimistic を呼ぶこと
 * （呼び忘れても ReservationSyncService 側の TTL で自動解除される）。
 */
export function beginOptimistic(updates: Reservation[]): void {
  reservationSyncService.beginLocalWrite(updates.map(r => r.id));
  writeAndNotify(updates);
}

/**
 * 保存成功。サーバーレスポンスをキャッシュへ反映して保護を解除する。
 *
 * @param ids   保護を解除する予約 id（レスポンスが無い場合の取りこぼし防止）
 * @param saved saveDispatchReservation のレスポンス（サーバー確定値）。
 *              null/undefined なら保護解除のみ行う。
 */
export function settleOptimistic(ids: Array<string | number>, saved?: Reservation | null): void {
  // applyLocalUpdate は id ごとに pending を解除し、リスナー通知まで行う
  if (saved && (saved as any).id != null) {
    reservationSyncService.applyLocalUpdate(saved);
  }
  reservationSyncService.endLocalWrite(ids);
}

/** 保存失敗。元の値をキャッシュへ戻して保護を解除する */
export function rollbackOptimistic(originals: Reservation[]): void {
  reservationSyncService.endLocalWrite(originals.map(r => r.id));
  writeAndNotify(originals);
}
